<template>
  <!-- Toggle Button -->
  <button
    class="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-primary text-white p-2 rounded-l-md shadow-lg hover:bg-primary/90 transition-colors"
    @click="togglePlayground"
  >
    <Icon :path="mdiCodeBraces" class="w-6 h-6" />
  </button>

  <!-- Playground Panel -->
  <div
    v-if="isOpen"
    class="fixed inset-0 flex z-50"
    @keydown.esc="isOpen = false"
  >
    <!-- Backdrop -->
    <div class="flex-1 bg-black bg-opacity-50" @click="isOpen = false"></div>
    <!-- Drawer -->
    <div
      class="w-full md:w-96 lg:w-[500px] bg-white dark:bg-gray-800 shadow-lg relative p-6 overflow-y-auto flex flex-col"
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Model Playground
        </h2>
        <button
          class="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          @click="isOpen = false"
        >
          <Icon :path="mdiClose" class="w-5 h-5" />
        </button>
      </div>

      <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
        You can use this playground to learn, predict, and get metrics directly
        from the browser.
      </p>

      <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
        To interact with this model from your application, please refer to the
        <router-link
          :to="{ name: 'api-keys' }"
          class="text-primary hover:text-primary-dark dark:text-primary dark:hover:text-primary-dark underline"
          >API keys</router-link
        >
        page and the documentation.
      </p>

      <!-- Tabs -->
      <div class="mb-4 border-b border-gray-200 dark:border-gray-700">
        <nav class="-mb-px flex space-x-4" aria-label="Tabs">
          <button
            v-for="tab in tabs"
            :key="tab.name"
            :class="[
              activeTab === tab.name
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600',
              'whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm',
            ]"
            @click="activeTab = tab.name"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>

      <!-- Tab Content -->
      <div class="flex-grow overflow-y-auto">
        <!-- Learn Section -->
        <section v-show="activeTab === 'learn'">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Update the model with a new data point.
          </p>

          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Request Body</label
          >
          <textarea
            v-model="learnInput"
            rows="8"
            class="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
          ></textarea>

          <button
            class="mt-3 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm w-full"
            :disabled="playgroundLoading"
            @click="runLearn"
          >
            Learn
          </button>

          <PlaygroundResponse
            :loading="playgroundLoading && currentAction === 'learn'"
            :error="
              playgroundError && currentAction === 'learn'
                ? playgroundError
                : null
            "
            :response="
              playgroundResponse && currentAction === 'learn'
                ? playgroundResponse
                : null
            "
            class="mt-4"
          />

          <PlaygroundCollapsibleExample
            title="Example Request (JSON)"
            class="mt-4"
          >
            <pre
              class="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto font-mono"
              >{{ learnExampleRequest }}</pre
            >
          </PlaygroundCollapsibleExample>
        </section>

        <!-- Predict Section -->
        <section v-show="activeTab === 'predict'">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Make a prediction based on input features.
          </p>

          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Request Body</label
          >
          <textarea
            v-model="predictInput"
            rows="8"
            class="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
          ></textarea>

          <button
            class="mt-3 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm w-full"
            :disabled="playgroundLoading"
            @click="runPredict"
          >
            Predict
          </button>

          <PlaygroundResponse
            :loading="playgroundLoading && currentAction === 'predict'"
            :error="
              playgroundError && currentAction === 'predict'
                ? playgroundError
                : null
            "
            :response="
              playgroundResponse && currentAction === 'predict'
                ? playgroundResponse
                : null
            "
            class="mt-4"
          />

          <PlaygroundCollapsibleExample
            title="Example Request (JSON)"
            class="mt-4"
          >
            <pre
              class="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto font-mono"
              >{{ predictExampleRequest }}</pre
            >
          </PlaygroundCollapsibleExample>
        </section>

        <!-- Forecast Section -->
        <section v-show="activeTab === 'forecast'">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
            Generate forecasts based with or without input features for a given
            horizon.
          </p>

          <label
            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >Request Body</label
          >
          <textarea
            v-model="forecastInput"
            rows="13"
            class="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-primary focus:border-primary"
          ></textarea>

          <button
            class="mt-3 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm w-full"
            :disabled="playgroundLoading"
            @click="runForecast"
          >
            Forecast
          </button>

          <PlaygroundResponse
            :loading="playgroundLoading && currentAction === 'forecast'"
            :error="
              playgroundError && currentAction === 'forecast'
                ? playgroundError
                : null
            "
            :response="
              playgroundResponse && currentAction === 'forecast'
                ? playgroundResponse
                : null
            "
            class="mt-4"
          />

          <PlaygroundCollapsibleExample
            title="Example Request (JSON)"
            class="mt-4"
          >
            <pre
              class="text-xs bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-auto font-mono"
              >{{ forecastExampleRequest }}</pre
            >
          </PlaygroundCollapsibleExample>
        </section>

        <!-- Metrics Section -->
        <section v-show="activeTab === 'metrics'">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">
            View current model performance metrics.
          </p>
          <button
            class="mt-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm w-full"
            :disabled="playgroundLoading"
            @click="fetchMetrics(model.id)"
          >
            Get metrics
          </button>

          <div
            v-if="playgroundLoading && currentAction === 'metrics'"
            class="mt-4 text-center text-gray-500"
          >
            Loading...
          </div>
          <pre
            v-else-if="model.metrics"
            class="mt-4 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm overflow-auto"
            >{{ JSON.stringify(model.metrics, null, 2) }}
          </pre>
          <div v-else class="mt-4 text-gray-500">No metrics available.</div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { mdiClose, mdiCodeBraces } from "@mdi/js";
import kappamlAPI from "@/services/kappaml-api";
import PlaygroundResponse from "@/components/PlaygroundResponse.vue";
import PlaygroundCollapsibleExample from "@/components/PlaygroundCollapsibleExample.vue";
import { analytics } from "@/services/firebase";
import { logEvent } from "firebase/analytics";

export default {
  name: "Playground",
  components: {
    PlaygroundResponse,
    PlaygroundCollapsibleExample,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  emits: ["update:model"],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const playgroundInput = ref("{}");
    const playgroundResponse = ref(null);
    const playgroundLoading = ref(false);
    const playgroundError = ref(null);
    const activeTab = ref("learn");
    const learnInput = ref("");
    const predictInput = ref("");
    const forecastInput = ref("");
    const currentAction = ref("learn");
    const isForecastingModel = ref(false);
    const learnExampleRequest = ref("");
    const predictExampleRequest = ref("");
    const forecastExampleRequest = ref("");

    const togglePlayground = () => {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        logEvent(analytics, "playground_opened", {
          model_id: props.model.id,
          model_name: props.model.name,
          model_type: props.model.ml_type,
        });
      }
    };

    const setExampleData = () => {
      learnExampleRequest.value = JSON.stringify(
        {
          features: { feat1: 0.5, feat2: 5 },
          target: 1.2,
        },
        null,
        2,
      );

      predictExampleRequest.value = JSON.stringify(
        { features: { feat1: 0.6, feat2: 5 } },
        null,
        2,
      );

      if (isForecastingModel.value) {
        forecastExampleRequest.value = JSON.stringify(
          {
            features: [
              { feat1: 0.7, feat2: 6 },
              { feat1: 0.9, feat2: 7 },
            ],
            horizon: 2,
          },
          null,
          2,
        );
      }

      // Pre-fill inputs with examples
      learnInput.value = learnExampleRequest.value;
      predictInput.value = predictExampleRequest.value;
      if (isForecastingModel.value) {
        forecastInput.value = forecastExampleRequest.value;
      }
    };

    // Watch only the model type so inputs are not reset by unrelated model updates
    watch(
      () => props.model?.ml_type,
      (newType, oldType) => {
        isForecastingModel.value = newType === "forecasting";
        if (oldType === undefined || newType !== oldType) {
          setExampleData();
        }
      },
      { immediate: true },
    );

    const fetchMetrics = async (modelId) => {
      try {
        const metricsData = await kappamlAPI.getModelMetrics(modelId);
        if (props.model) {
          const updatedModel = { ...props.model, metrics: metricsData };
          emit("update:model", updatedModel);
        }
      } catch (err) {
        console.error("Error fetching metrics:", err);
      }
    };

    const runLearn = async () => {
      playgroundError.value = null;
      playgroundResponse.value = null;
      currentAction.value = "learn";
      try {
        const payload = JSON.parse(learnInput.value);
        playgroundResponse.value = await kappamlAPI.learnModel(
          props.model.id,
          payload,
        );
      } catch (err) {
        playgroundError.value = err.message;
      } finally {
        playgroundLoading.value = false;
      }
    };

    const runPredict = async () => {
      playgroundError.value = null;
      playgroundResponse.value = null;
      currentAction.value = "predict";
      try {
        const payload = JSON.parse(predictInput.value);
        playgroundResponse.value = await kappamlAPI.predictModel(
          props.model.id,
          payload,
        );
      } catch (err) {
        playgroundError.value = err.message;
      } finally {
        playgroundLoading.value = false;
      }
    };

    const runForecast = async () => {
      playgroundError.value = null;
      playgroundResponse.value = null;
      currentAction.value = "forecast";
      try {
        const payload = JSON.parse(forecastInput.value);
        playgroundResponse.value = await kappamlAPI.forecastModel(
          props.model.id,
          payload,
        );
      } catch (err) {
        playgroundError.value = err.message;
      } finally {
        playgroundLoading.value = false;
      }
    };

    const tabs = computed(() => {
      const baseTabs = [
        { name: "learn", label: "Learn" },
        { name: "predict", label: "Predict" },
      ];

      if (isForecastingModel.value) {
        baseTabs.push({ name: "forecast", label: "Forecast" });
      }

      baseTabs.push({ name: "metrics", label: "Metrics" });
      return baseTabs;
    });

    return {
      isOpen,
      mdiClose,
      mdiCodeBraces,
      playgroundInput,
      playgroundResponse,
      playgroundLoading,
      playgroundError,
      activeTab,
      tabs,
      learnInput,
      predictInput,
      forecastInput,
      currentAction,
      isForecastingModel,
      learnExampleRequest,
      predictExampleRequest,
      forecastExampleRequest,
      runLearn,
      runPredict,
      runForecast,
      fetchMetrics,
      togglePlayground,
    };
  },
};
</script>
